import { useMutation, useQueryClient } from "@tanstack/react-query";
import { API_ROUTES } from "../../constants";
import { apiClient } from "./apiClient";
import { QUERY_KEYS } from "./constants";
import type { DeleteResponse, ChatSession } from "./types";

/**
 * Hook to delete a specific chat session
 */
export const useDeleteSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await apiClient.delete<DeleteResponse>(
        API_ROUTES.omniSearch.chat(sessionId)
      );
      return response.data;
    },
    // before the mutation has been sent
    onMutate: async (sessionId: string) => {
      // Cancel any outgoing fetches for getSessions
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: QUERY_KEYS.getSessions,
      });

      // Snapshot the previous value
      const previousSessions = queryClient.getQueryData<ChatSession[]>(
        QUERY_KEYS.getSessions
      );

      // Optimistically update the session list
      queryClient.setQueryData<ChatSession[]>(
        QUERY_KEYS.getSessions,
        (prevSessions) => {
          if (!prevSessions) {
            return [];
          }
          return prevSessions.filter((session) => session.id !== sessionId);
        }
      );

      return { previousSessions };
    },

    // rollback on error
    onError: (_, __, context) => {
      if (context?.previousSessions) {
        queryClient.setQueryData(
          QUERY_KEYS.getSessions,
          context.previousSessions
        );
      }
    },

    // refetch after mutation only if needed
    onSettled: async () => {
      // Only invalidate if we want to ensure server state is accurate
      // This is optional since we already did optimistic updates
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.getSessions });
    },
  });
};
